import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Facebook, Instagram, Menu, Music2, Phone, Search, X } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, SOCIALS } from "@/data/services";

const LOGO_MARK = "/logo-red.jpg";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about-us", label: "About Us" },
  { to: "/#services", label: "Services" },
  { to: "/packages", label: "Packages" },
  { to: "/gallery", label: "Gallery" },
  { to: "/gifts", label: "Gifts" },
  { to: "/deals", label: "Deals" },
  { to: "/contact-us", label: "Contact Us" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (window.location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 120);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <header data-testid="site-nav" className="fixed top-0 inset-x-0 z-50">
      <div className="hidden md:block bg-[#1B1B1E]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-end gap-4">
          <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" data-testid="topbar-instagram" aria-label="Instagram" className="text-slate-300 hover:text-[#FF4311] transition-colors">
            <Instagram className="w-4 h-4" />
          </a>
          <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" data-testid="topbar-facebook" aria-label="Facebook" className="text-slate-300 hover:text-[#FF4311] transition-colors">
            <Facebook className="w-4 h-4" />
          </a>
        </div>
      </div>

      <div className="bg-[#0A0A0C]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-[76px] flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-logo" className="flex items-center">
            <img src={LOGO_MARK} alt="Brad's Mobile Detailing" className="h-10 md:h-12 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => go(l.to)}
                className="font-display uppercase text-[13px] font-medium tracking-[0.08em] text-slate-200 hover:text-[#FF4311] transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-search-button"
              onClick={() => go("/contact-us")}
              aria-label="Search"
              className="hidden lg:block p-2 text-slate-200 hover:text-[#FF4311] transition-colors"
            >
              <Search className="w-4 h-4" />
            </button>
            <Link
              to="/schedule"
              data-testid="nav-quote-button"
              className="hidden sm:inline-block bg-[#EA0A0B] hover:bg-[#C20A0B] text-white font-display uppercase text-[13px] font-medium tracking-[0.08em] px-6 py-3 transition-colors"
            >
              Get a Quote
            </Link>
            <Link
              to="/book"
              data-testid="nav-book-button"
              className="inline-block bg-[#EA0A0B] hover:bg-[#C20A0B] text-white font-display uppercase text-[13px] font-medium tracking-[0.08em] px-6 py-3 transition-colors"
            >
              Book Now
            </Link>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-slate-200"
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div data-testid="nav-mobile-menu" className="lg:hidden border-t border-white/10 bg-[#0A0A0C]">
            <div className="px-4 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.label}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => go(l.to)}
                  className="text-left font-display uppercase text-sm tracking-[0.1em] text-slate-200 py-3 px-2 hover:text-[#FF4311] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={PHONE_TEL}
                data-testid="nav-mobile-call-button"
                className="flex items-center gap-2 font-display uppercase text-sm tracking-[0.1em] text-[#FF4311] py-3 px-2"
              >
                <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
